import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import AppShell from "../components/AppShell";
import { Package, WarningCircle, CheckCircle, Lock, Flag, ChartLine, Warehouse, ArrowRight } from "@phosphor-icons/react";

function Stat({ label, value, icon: Icon, accent = "zinc" }) {
  const color = {
    emerald: "text-emerald-800 bg-emerald-100 border-emerald-300",
    rose: "text-rose-800 bg-rose-100 border-rose-300",
    zinc: "text-zinc-800 bg-zinc-100 border-zinc-300",
    indigo: "text-indigo-50 bg-indigo-900 border-indigo-700",
    amber: "text-amber-900 bg-amber-100 border-amber-300",
  }[accent];
  return (
    <div className="rounded-sm border border-zinc-300 bg-white p-4">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.08em] text-zinc-500 font-semibold">{label}</div>
          <div className="font-mono text-3xl mt-1 text-zinc-900" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>{value}</div>
        </div>
        <div className={`w-9 h-9 rounded-sm grid place-items-center border ${color}`}>
          <Icon weight="fill" className="w-4 h-4" />
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);

  const load = async () => {
    const { data: s } = await api.get("/sessions");
    setSessions(s);
    const pick = s.find((x) => x.session_status === "active") || s[0];
    setActiveSession(pick || null);
    if (pick) {
      const { data: sum } = await api.get(`/sessions/${pick.id}/summary`);
      setSummary(sum);
    }
    const { data: logs } = await api.get("/audit-logs", { params: { limit: 8 } });
    setRecent(logs);
  };

  useEffect(() => { load(); }, []);

  return (
    <AppShell>
      <div className="max-w-[1600px] mx-auto p-3 sm:p-6 space-y-6">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 font-semibold">Admin</div>
            <h1 className="font-display text-3xl font-bold text-zinc-900 tracking-tight">Dashboard</h1>
            <div className="text-sm text-zinc-600 mt-1">
              {activeSession ? <>Active session: <span className="font-mono text-zinc-900" data-testid="active-session-name">{activeSession.session_name}</span> · <span className="uppercase text-[11px] tracking-wider">{activeSession.session_status}</span></> : "No sessions yet — create one to begin."}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin/sessions" className="inline-flex items-center gap-1.5 rounded-sm bg-indigo-950 hover:bg-indigo-900 text-white px-3 py-2 text-sm" data-testid="go-sessions-button"><Warehouse className="w-4 h-4" /> Sessions</Link>
            <Link to="/admin/reports" className="inline-flex items-center gap-1.5 rounded-sm border border-zinc-300 hover:bg-zinc-100 text-zinc-900 px-3 py-2 text-sm" data-testid="go-reports-button"><ChartLine className="w-4 h-4" /> Reports</Link>
          </div>
        </div>

        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <Stat label="Total SKUs" value={summary.total} icon={Package} />
            <Stat label="Matched" value={summary.by_status.matched} icon={CheckCircle} accent="emerald" />
            <Stat label="Recount" value={summary.by_status.recount_required} icon={WarningCircle} accent="rose" />
            <Stat label="Pending" value={summary.by_status.pending + summary.by_status.in_progress} icon={WarningCircle} accent="amber" />
            <Stat label="Escalated" value={summary.by_status.locked_awaiting_head_counter} icon={Lock} accent="zinc" />
            <Stat label="Finalized" value={summary.by_status.finalized_by_head_counter} icon={Flag} accent="indigo" />
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 rounded-sm border border-zinc-300 bg-white">
            <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
              <div className="font-display font-semibold text-zinc-900">All sessions</div>
              <Link to="/admin/sessions" className="text-xs text-indigo-900 hover:underline">Manage →</Link>
            </div>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr><th>Session</th><th>Warehouse</th><th>Status</th><th>SKUs</th><th>Created</th><th></th></tr>
                </thead>
                <tbody>
                  {sessions.slice(0, 8).map((s) => (
                    <tr key={s.id} data-testid={`session-row-${s.id}`}>
                      <td className="font-medium">{s.session_name}</td>
                      <td className="mono">{s.warehouse_name || "—"}</td>
                      <td><span className="uppercase text-[11px] tracking-wider">{s.session_status}</span></td>
                      <td className="mono">{s.item_count}</td>
                      <td className="mono text-zinc-500">{new Date(s.created_at).toLocaleDateString()}</td>
                      <td><Link to={`/admin/sessions/${s.id}`} className="text-indigo-900 text-xs inline-flex items-center gap-1">Open <ArrowRight className="w-3 h-3" /></Link></td>
                    </tr>
                  ))}
                  {sessions.length === 0 && (
                    <tr><td colSpan={6} className="text-center text-zinc-500 py-10">No sessions yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-sm border border-zinc-300 bg-white">
            <div className="px-4 py-3 border-b border-zinc-200 font-display font-semibold text-zinc-900">Recent activity</div>
            <ul className="divide-y divide-zinc-200 max-h-[420px] overflow-y-auto">
              {recent.map((a) => (
                <li key={a.id} className="px-4 py-2.5 text-sm flex items-start justify-between gap-3">
                  <div>
                    <div className="text-zinc-900"><span className="font-mono text-[12px] text-zinc-500">{a.action_type}</span> · {a.user_full_name || a.user_email || "system"}</div>
                    <div className="text-[11px] text-zinc-500 mono">{new Date(a.created_at).toLocaleString()}</div>
                  </div>
                </li>
              ))}
              {recent.length === 0 && <li className="px-4 py-8 text-center text-zinc-500 text-sm">No recent activity.</li>}
            </ul>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
